import React from 'react';
import PropTypes from 'prop-types';
import { Input } from '../../Atoms'; // adjust path if needed

const FormField = ({
  label,
  name,
  type = 'text',
  as = 'input', // input, select, textarea
  value,
  onChange,
  placeholder,
  helpText,
  error,
  required = false,
  disabled = false,
  options = [],
  className = '',
  ...props
}) => {
  const wrapperClasses = ['formfield', className].filter(Boolean).join(' ');

  const describedById = helpText && !error ? `${name}-help` : error ? `${name}-error` : undefined;

  const commonControlProps = {
    id: name,
    name,
    value,
    onChange,
    placeholder,
    required,
    disabled,
    'aria-invalid': Boolean(error) || undefined,
    'aria-describedby': describedById,
    ...props,
  };

  const renderControl = () => {
    switch (as) {
      case 'textarea':
        return (
          <Input
            as="textarea"
            error={Boolean(error)}
            {...commonControlProps}
          />
        );

      case 'select':
        // Until you have a Select atom: keep select but style it like input via className
        return (
          <select
            {...commonControlProps}
            className={['input', error ? 'input-error' : ''].filter(Boolean).join(' ')}
          >
            {placeholder && <option value="">{placeholder}</option>}
            {options.map((option) => {
              const opt = typeof option === 'object'
                ? option
                : { value: option, label: option };

              return (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              );
            })}
          </select>
        );

      default:
        return (
          <Input
            type={type}
            error={Boolean(error)}
            {...commonControlProps}
          />
        );
    }
  };

  return (
    <div className={wrapperClasses}>
      {label && (
        <label htmlFor={name} className="text-label">
          {label}
          {required && <span className="formfield-required">*</span>}
        </label>
      )}

      {renderControl()}

      {helpText && !error && (
        <p id={`${name}-help`} className="text-body-sm text-muted">
          {helpText}
        </p>
      )}

      {error && (
        <p id={`${name}-error`} className="text-body-sm text-accent">
          {error}
        </p>
      )}
    </div>
  );
};

FormField.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,

  type: PropTypes.string,
  as: PropTypes.oneOf(['input', 'select', 'textarea']),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,

  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      }),
    ])
  ),

  helpText: PropTypes.string,
  error: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,

  className: PropTypes.string,
};

export default FormField;
